import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const PrecisionAgriculture = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/images/services/precision_agriculture.png')}
                        alt="Precision Agriculture"
                        className="w-full h-full object-cover opacity-70"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-thin mb-6"
                    >
                        Precision <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">Agriculture</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Intelligent Farming Designed for Performance
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-2/3"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            <span className="font-semibold text-gray-900">Precision Agriculture</span> at Marvelous Agri Solution is a next-generation farming system built on data, science, and smart technology.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We transform traditional fields into intelligent ecosystems where every decision is calculated, every input is optimized, and every acre performs at its highest potential.
                        </p>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border-l-4 border-green-500 mb-10">
                            <h3 className="text-lg font-bold text-gray-900 mb-6">
                                By integrating soil analytics, satellite mapping, climate intelligence, and predictive crop modeling, we ensure:
                            </h3>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {[
                                    "Accurate nutrient management",
                                    "Smart irrigation scheduling",
                                    "Early pest & disease alerts",
                                    "Optimized input utilization",
                                    "Higher yield consistency"
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-center gap-3 text-gray-700 font-light">
                                        <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex flex-wrap gap-4 mb-10">
                            <div className="bg-red-50 text-red-700 px-6 py-3 rounded-full font-medium shadow-sm border border-red-100 flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-red-500"></span> No overuse.
                            </div>
                            <div className="bg-red-50 text-red-700 px-6 py-3 rounded-full font-medium shadow-sm border border-red-100 flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-red-500"></span> No blind decisions.
                            </div>
                            <div className="bg-red-50 text-red-700 px-6 py-3 rounded-full font-medium shadow-sm border border-red-100 flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-red-500"></span> No unnecessary cost.
                            </div>
                        </div>

                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            Every drop of water, every gram of fertilizer, and every management action is guided by measurable intelligence.
                        </p>

                        <p className="text-gray-600 text-lg leading-relaxed mb-10 font-light border-l-4 border-emerald-500 pl-6 py-4 bg-emerald-50 rounded-r-2xl">
                            <span className="font-semibold text-gray-900">Precision Agriculture</span> eliminates uncertainty and replaces it with strategy — delivering sustainable productivity, improved soil health, and long-term profitability.
                        </p>

                        <div className="bg-gray-900 p-8 rounded-2xl shadow-xl text-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10"></div>
                            <p className="text-2xl font-light text-gray-300 mb-2 relative z-10">
                                This is not just advanced farming.
                            </p>
                            <h3 className="text-3xl font-bold text-white mb-10 relative z-10">
                                This is farming engineered for excellence.
                            </h3>
                            <div className="flex flex-col sm:flex-row justify-center gap-8 relative z-10">
                                <div className="text-center">
                                    <div className="bg-green-500/20 text-green-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                        </svg>
                                    </div>
                                    <p className="text-green-300 font-medium">Measured Inputs.</p>
                                </div>
                                <div className="text-center">
                                    <div className="bg-emerald-500/20 text-emerald-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                        </svg>
                                    </div>
                                    <p className="text-emerald-300 font-medium">Monitored Growth.</p>
                                </div>
                                <div className="text-center">
                                    <div className="bg-teal-500/20 text-teal-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                        </svg>
                                    </div>
                                    <p className="text-teal-300 font-medium">Maximized Results.</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/3 sticky top-24"
                    >
                        <div className="bg-white p-2 rounded-2xl shadow-2xl skew-y-2 transform hover:skew-y-0 transition-transform duration-500">
                            <img
                                src={require('../../../assets/images/services/precision_agriculture.png')}
                                alt="Precision Ag"
                                className="rounded-xl w-full h-auto object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default PrecisionAgriculture;
